import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './Header.css';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleDropdown = (menu: string) => {
    setActiveDropdown(activeDropdown === menu ? null : menu);
  };

  // no header widgets; kept minimal state for nav

  // Animation variants for dropdowns
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dropdownVariants = {
    closed: { 
      opacity: 0, 
      height: 0,
      overflow: 'hidden'
    },
    open: { 
      opacity: 1, 
      height: 'auto',
      overflow: 'visible',
      transition: { 
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  return (
    <motion.header 
      className="header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* header top widgets removed (ticker and market components) */}
      <div className="header-container">
        {/* logo removed as requested */}
        
        <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
          <ul className="nav-list">
            <li className="nav-item">
              <Link to="/" onClick={() => setIsMenuOpen(false)} className="home-icon">
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" fill="none">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/markets" onClick={() => setIsMenuOpen(false)}>Markets</Link>
              <AnimatePresence>
                {activeDropdown === 'markets' && (
                  <motion.div 
                    className="dropdown"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ul>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/markets/forex" onClick={() => setIsMenuOpen(false)}>Forex</Link></motion.li>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/markets/indices" onClick={() => setIsMenuOpen(false)}>Indices</Link></motion.li>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/markets/commodity" onClick={() => setIsMenuOpen(false)}>Commodity</Link></motion.li>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/markets/crypto" onClick={() => setIsMenuOpen(false)}>Crypto Currency</Link></motion.li>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/markets/stocks" onClick={() => setIsMenuOpen(false)}>Stocks</Link></motion.li>
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
            
            <li className="nav-item">
              <Link to="/services" onClick={() => setIsMenuOpen(false)}>Services</Link>
              <AnimatePresence>
                {activeDropdown === 'services' && (
                  <motion.div 
                    className="dropdown"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ul>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/services/education" onClick={() => setIsMenuOpen(false)}>Education</Link></motion.li>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/services/webinar" onClick={() => setIsMenuOpen(false)}>Webinar Training</Link></motion.li>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/services/signals" onClick={() => setIsMenuOpen(false)}>Free Trading Signals</Link></motion.li>
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
            
            <li className="nav-item">
              <Link to="/education" onClick={() => setIsMenuOpen(false)}>Education</Link>
              <AnimatePresence>
                {activeDropdown === 'education' && (
                  <motion.div 
                    className="dropdown"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ul>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/education/forex" onClick={() => setIsMenuOpen(false)}>Forex</Link></motion.li>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/education/commodity" onClick={() => setIsMenuOpen(false)}>Commodity</Link></motion.li>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/education/indices" onClick={() => setIsMenuOpen(false)}>Indices</Link></motion.li>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/education/stocks" onClick={() => setIsMenuOpen(false)}>Stocks</Link></motion.li>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/education/crypto" onClick={() => setIsMenuOpen(false)}>Crypto Currency</Link></motion.li>
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
            
            <li className="nav-item">
              <Link to="/platforms" onClick={() => setIsMenuOpen(false)}>Platforms</Link>
              <AnimatePresence>
                {activeDropdown === 'platforms' && (
                  <motion.div 
                    className="dropdown"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ul>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/platforms/mt5" onClick={() => setIsMenuOpen(false)}>MT5</Link></motion.li>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/platforms/mt4" onClick={() => setIsMenuOpen(false)}>MT4</Link></motion.li>
                      <motion.li whileHover={{ backgroundColor: "#112240" }}><Link to="/platforms/ctraders" onClick={() => setIsMenuOpen(false)}>CTraders</Link></motion.li>
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
            
            <li className="nav-item">
              <Link to="/ib-partners" onClick={() => setIsMenuOpen(false)}>IB Partners</Link>
            </li>
            
            <li className="nav-item">
              <Link to="/about" onClick={() => setIsMenuOpen(false)}>About Us</Link>
            </li>
            
            <li className="nav-item">
              <Link to="/contact" onClick={() => setIsMenuOpen(false)}>Contact Us</Link>
            </li>
            

          </ul>
        </nav>
        
        <motion.div 
          className="hamburger" 
          onClick={toggleMenu}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <span></span>
          <span></span>
          <span></span>
        </motion.div>
      </div>
    </motion.header>
  );
};

export default Header;